import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { 
  CalendarIcon, 
  UserGroupIcon, 
  CreditCardIcon, 
  ShieldCheckIcon,
  ClockIcon,
  BanknotesIcon,
  ExclamationCircleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { format, addDays } from 'date-fns';
import { useMutation } from 'react-query';
import axios from 'axios';

const BookingForm = ({ tour }) => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem('token'));
  
  // Calculate payment due date (7 days from now)
  const paymentDueDate = format(addDays(new Date(), 7), 'yyyy-MM-dd');
  
  // State for showing pay later info
  const [showPayLaterInfo, setShowPayLaterInfo] = useState(false);
  
  // Initialize formik
  const formik = useFormik({
    initialValues: {
      booking_date: '',
      adults: 1,
      children: 0,
      special_requirements: '',
      contact_name: '',
      contact_email: '',
      contact_phone: '',
      payment_method: 'mpesa',
      pay_later: false,
      payment_due_date: paymentDueDate
    },
    validationSchema: Yup.object({
      booking_date: Yup.date()
        .required('Please select a date')
        .min(new Date(), 'Date cannot be in the past'),
      adults: Yup.number()
        .required('Please enter number of adults')
        .min(1, 'Minimum 1 adult')
        .max(tour.group_size || 10, `Maximum ${tour.group_size || 10} travelers`),
      children: Yup.number()
        .min(0, 'Cannot be negative')
        .required('Please enter number of children'),
      special_requirements: Yup.string()
        .max(500, 'Maximum 500 characters'),
      contact_name: Yup.string()
        .required('Please enter your name'),
      contact_email: Yup.string()
        .email('Invalid email address')
        .required('Please enter your email'),
      contact_phone: Yup.string()
        .required('Please enter your phone number'),
      payment_method: Yup.string()
        .required('Please select a payment method')
        .oneOf(['mpesa', 'card', 'bank_transfer', 'paypal', 'office_payment'], 'Invalid payment method'),
      pay_later: Yup.boolean(),
      payment_due_date: Yup.date().when('pay_later', {
        is: true,
        then: () => Yup.date().required('Payment due date is required')
      })
    }),
    onSubmit: (values) => {
      if (!isLoggedIn) {
        // Redirect to login page if not logged in
        localStorage.setItem('bookingIntent', JSON.stringify({
          tour_id: tour.id,
          ...values
        }));
        navigate('/login?redirect=booking');
        return;
      }
      
      // Get current date for booking_date if not set
      const today = new Date().toISOString().split('T')[0];
      
      // Prepare booking data with all required fields
      const bookingData = {
        tour_id: tour.id,
        booking_date: values.booking_date || today,
        travel_date: values.booking_date || today, // Use booking_date as travel_date if not separately specified
        adults: values.adults,
        children: values.children || 0,
        special_requirements: values.special_requirements || '',
        contact_name: values.contact_name,
        contact_email: values.contact_email,
        contact_phone: values.contact_phone,
        payment_method: values.payment_method,
        total_price: calculateTotal(values.adults, values.children)
      };
      
      // Handle pay later option
      if (values.pay_later) {
        bookingData.payment_method = 'office_payment';
        bookingData.payment_due_date = values.payment_due_date;
      }
      
      console.log('Submitting booking data:', bookingData);
      
      // Submit booking
      bookingMutation.mutate(bookingData);
    }
  });
  
  // State for backend validation errors
  const [backendErrors, setBackendErrors] = useState({});

  // Booking mutation
  const bookingMutation = useMutation(
    async (bookingData) => {
      // Clear any previous backend errors
      setBackendErrors({});
      
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      console.log('API URL:', apiUrl);
      const response = await axios.post(`${apiUrl}/api/v1/bookings`, bookingData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      return response.data;
    },
    {
      onSuccess: (data) => {
        // Log the complete response structure to understand the data format
        console.log('Booking successful, complete response:', JSON.stringify(data, null, 2));
        
        toast.success(formik.values.pay_later ? 
          'Tour reserved successfully! Remember to complete payment within 7 days.' : 
          'Booking successful!');
        
        // Use the redirect URL from the backend if available, otherwise use a default
        const redirectUrl = data.redirect_url || '/dashboard/bookings';
        console.log('Redirecting to:', redirectUrl);
        
        // Add a small delay before navigation to ensure the toast is seen
        setTimeout(() => {
          window.location.href = redirectUrl; // Use direct location change instead of navigate
        }, 1500);
      },
      onError: (error) => {
        console.error('Booking error:', error);
        
        // Handle validation errors from backend
        if (error.response && error.response.status === 422) {
          console.log('Error response:', error.response.data);
          console.log('Full error details:', JSON.stringify(error.response.data, null, 2));
          
          if (error.response.data.errors) {
            // Set backend errors state
            setBackendErrors(error.response.data.errors);
            
            // Also set formik errors
            const formikErrors = {};
            Object.keys(error.response.data.errors).forEach(key => {
              formikErrors[key] = error.response.data.errors[key][0];
              console.log(`Validation error for ${key}: ${error.response.data.errors[key][0]}`);
            });
            formik.setErrors(formikErrors);
            
            // Show the first error message as a toast
            const firstErrorField = Object.keys(error.response.data.errors)[0];
            const firstErrorMessage = error.response.data.errors[firstErrorField][0];
            toast.error(firstErrorMessage);
          } else {
            toast.error(error.response.data.message || 'Validation failed');
          }
        } else {
          toast.error('Failed to create booking. Please try again.');
        }
      }
    }
  );
  
  // Calculate total price based on number of travelers and tour price
  const calculateTotal = (adults, children) => {
    const adultPrice = tour.discount_price || tour.price;
    const childPrice = tour.child_price || (adultPrice * 0.7); // 70% of adult price if not specified
    return (adults * adultPrice) + (children * childPrice);
  };
  
  // Handle traveler count change
  const handleTravelerChange = (change) => {
    const newValue = formik.values.adults + change;
    if (newValue >= 1 && newValue <= tour.group_size) {
      formik.setFieldValue('adults', newValue);
    }
  };
  
  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-8">
      <h3 className="text-xl font-bold mb-6">Book This Tour</h3>
      
      <div className="mb-6">
        <div className="text-3xl font-bold text-gray-800 mb-2">
          ${tour.price} <span className="text-sm font-normal text-gray-500">per person</span>
        </div>
        
        <div className="flex items-center text-sm text-gray-500 mb-4">
          <ClockIcon className="h-4 w-4 mr-1" />
          <span>{tour.duration} {tour.duration === 1 ? 'day' : 'days'}</span>
        </div>
      </div>
      
      <form onSubmit={formik.handleSubmit}>
        {/* Date selection */}
        <div className="mb-4">
          <label htmlFor="booking_date" className="block text-sm font-medium text-gray-700 mb-1">
            When would you like to go?
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <CalendarIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="date"
              id="booking_date"
              name="booking_date"
              value={formik.values.booking_date}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              min={new Date().toISOString().split('T')[0]}
              className={`block w-full pl-10 pr-3 py-2 border ${
                formik.touched.booking_date && formik.errors.booking_date
                  ? 'border-red-500'
                  : 'border-gray-300'
              } rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent`}
            />
          </div>
          {formik.touched.booking_date && formik.errors.booking_date && (
            <p className="mt-1 text-sm text-red-500">{formik.errors.booking_date}</p>
          )}
        </div>
        
        {/* Travelers */}
        <div className="mb-4">
          <label htmlFor="adults" className="block text-sm font-medium text-gray-700 mb-1">
            Number of Adults
          </label>
          <div className="flex items-center">
            <button
              type="button"
              onClick={() => handleTravelerChange(-1)}
              disabled={formik.values.adults <= 1}
              className={`px-3 py-2 border border-gray-300 rounded-l-md ${
                formik.values.adults <= 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              -
            </button>
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <UserGroupIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="number"
                id="adults"
                name="adults"
                value={formik.values.adults}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                min="1"
                max={tour.group_size}
                className={`block w-full pl-10 pr-3 py-2 border-t border-b ${
                  formik.touched.adults && formik.errors.adults
                    ? 'border-red-500'
                    : 'border-gray-300'
                } focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-center`}
              />
            </div>
            <button
              type="button"
              onClick={() => handleTravelerChange(1)}
              disabled={formik.values.adults >= tour.group_size}
              className={`px-3 py-2 border border-gray-300 rounded-r-md ${
                formik.values.adults >= tour.group_size
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              +
            </button>
          </div>
          {formik.touched.adults && formik.errors.adults && (
            <p className="mt-1 text-sm text-red-500">{formik.errors.adults}</p>
          )}
          <p className="mt-1 text-sm text-gray-500">
            Maximum {tour.group_size} travelers per booking
          </p>
        </div>
        
        {/* Children */}
        <div className="mb-4">
          <label htmlFor="children" className="block text-sm font-medium text-gray-700 mb-1">
            Number of Children
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <UserGroupIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="number"
              id="children"
              name="children"
              value={formik.values.children}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              min="0"
              className={`block w-full pl-10 pr-3 py-2 border ${
                formik.touched.children && formik.errors.children
                  ? 'border-red-500'
                  : 'border-gray-300'
              } rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent`}
            />
          </div>
          {formik.touched.children && formik.errors.children && (
            <p className="mt-1 text-sm text-red-500">{formik.errors.children}</p>
          )}
        </div>
        
        {/* Special Requirements */}
        <div className="mb-6">
          <label htmlFor="special_requirements" className="block text-sm font-medium text-gray-700 mb-1">
            Special Requirements (Optional)
          </label>
          <textarea
            id="special_requirements"
            name="special_requirements"
            value={formik.values.special_requirements}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            rows="3"
            className={`block w-full px-3 py-2 border ${
              formik.touched.special_requirements && formik.errors.special_requirements
                ? 'border-red-500'
                : 'border-gray-300'
            } rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent`}
            placeholder="Any dietary requirements, accessibility needs, etc."
          ></textarea>
          {formik.touched.special_requirements && formik.errors.special_requirements && (
            <p className="mt-1 text-sm text-red-500">{formik.errors.special_requirements}</p>
          )}
        </div>
        
        {/* Contact Information */}
        <div className="mb-6">
          <label htmlFor="contact_name" className="block text-sm font-medium text-gray-700 mb-1">
            Contact Name
          </label>
          <input
            type="text"
            id="contact_name"
            name="contact_name"
            value={formik.values.contact_name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className={`block w-full px-3 py-2 border ${
              formik.touched.contact_name && formik.errors.contact_name
                ? 'border-red-500'
                : 'border-gray-300'
            } rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent`}
          />
          {formik.touched.contact_name && formik.errors.contact_name && (
            <p className="mt-1 text-sm text-red-500">{formik.errors.contact_name}</p>
          )}
        </div>
        
        <div className="mb-6">
          <label htmlFor="contact_email" className="block text-sm font-medium text-gray-700 mb-1">
            Contact Email
          </label>
          <input
            type="email"
            id="contact_email"
            name="contact_email"
            value={formik.values.contact_email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className={`block w-full px-3 py-2 border ${
              formik.touched.contact_email && formik.errors.contact_email
                ? 'border-red-500'
                : 'border-gray-300'
            } rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent`}
          />
          {formik.touched.contact_email && formik.errors.contact_email && (
            <p className="mt-1 text-sm text-red-500">{formik.errors.contact_email}</p>
          )}
        </div>
        
        <div className="mb-6">
          <label htmlFor="contact_phone" className="block text-sm font-medium text-gray-700 mb-1">
            Contact Phone
          </label>
          <input
            type="text"
            id="contact_phone"
            name="contact_phone"
            value={formik.values.contact_phone}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className={`block w-full px-3 py-2 border ${
              formik.touched.contact_phone && formik.errors.contact_phone
                ? 'border-red-500'
                : 'border-gray-300'
            } rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent`}
          />
          {formik.touched.contact_phone && formik.errors.contact_phone && (
            <p className="mt-1 text-sm text-red-500">{formik.errors.contact_phone}</p>
          )}
        </div>
        
        {/* Pay Now or Pay Later Option */}
        <div className="mb-6">
          <div className="flex items-center justify-between bg-gray-50 p-4 rounded-lg mb-4">
            <div className="flex items-center">
              <input
                id="pay_later"
                name="pay_later"
                type="checkbox"
                checked={formik.values.pay_later}
                onChange={(e) => {
                  formik.setFieldValue('pay_later', e.target.checked);
                  setShowPayLaterInfo(e.target.checked);
                }}
                className="h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
              />
              <label htmlFor="pay_later" className="ml-2 block text-sm text-gray-700 font-medium">
                Pay Later (Reserve Now)
              </label>
            </div>
            <button
              type="button"
              onClick={() => setShowPayLaterInfo(!showPayLaterInfo)}
              className="text-primary text-sm hover:underline focus:outline-none flex items-center"
            >
              <InformationCircleIcon className="h-4 w-4 mr-1" />
              Info
            </button>
          </div>
          
          {showPayLaterInfo && (
            <div className="bg-blue-50 border border-blue-200 text-blue-800 p-4 rounded-lg mb-4">
              <div className="flex items-start">
                <InformationCircleIcon className="h-5 w-5 text-blue-500 mr-2 mt-0.5" />
                <div>
                  <h4 className="font-medium">Pay Later Information</h4>
                  <p className="text-sm mt-1">
                    By selecting "Pay Later", you can reserve your spot now and pay within 7 days. 
                    Your booking will be automatically canceled if payment is not received by the due date.
                  </p>
                  <p className="text-sm mt-2 font-medium">
                    Payment Due Date: {format(new Date(paymentDueDate), 'MMMM d, yyyy')}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Payment Method */}
        {!formik.values.pay_later && (
          <div className="mb-6">
            <label htmlFor="payment_method" className="block text-sm font-medium text-gray-700 mb-3">
              Payment Method
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div 
                className={`border ${formik.values.payment_method === 'mpesa' ? 'border-primary bg-primary bg-opacity-5' : 'border-gray-200'} rounded-lg p-3 cursor-pointer`}
                onClick={() => formik.setFieldValue('payment_method', 'mpesa')}
              >
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="mpesa"
                    name="payment_method"
                    value="mpesa"
                    checked={formik.values.payment_method === 'mpesa'}
                    onChange={formik.handleChange}
                    className="h-4 w-4 text-primary border-gray-300"
                  />
                  <label htmlFor="mpesa" className="ml-2 block text-sm font-medium text-gray-700 cursor-pointer">
                    M-Pesa
                  </label>
                </div>
              </div>
              
              <div 
                className={`border ${formik.values.payment_method === 'card' ? 'border-primary bg-primary bg-opacity-5' : 'border-gray-200'} rounded-lg p-3 cursor-pointer`}
                onClick={() => formik.setFieldValue('payment_method', 'card')}
              >
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="card"
                    name="payment_method"
                    value="card"
                    checked={formik.values.payment_method === 'card'}
                    onChange={formik.handleChange}
                    className="h-4 w-4 text-primary border-gray-300"
                  />
                  <label htmlFor="card" className="ml-2 block text-sm font-medium text-gray-700 cursor-pointer">
                    Card
                  </label>
                </div>
              </div>
              
              <div 
                className={`border ${formik.values.payment_method === 'bank_transfer' ? 'border-primary bg-primary bg-opacity-5' : 'border-gray-200'} rounded-lg p-3 cursor-pointer`}
                onClick={() => formik.setFieldValue('payment_method', 'bank_transfer')}
              >
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="bank_transfer"
                    name="payment_method"
                    value="bank_transfer"
                    checked={formik.values.payment_method === 'bank_transfer'}
                    onChange={formik.handleChange}
                    className="h-4 w-4 text-primary border-gray-300"
                  />
                  <label htmlFor="bank_transfer" className="ml-2 block text-sm font-medium text-gray-700 cursor-pointer">
                    Bank Transfer
                  </label>
                </div>
              </div>
              
              <div 
                className={`border ${formik.values.payment_method === 'paypal' ? 'border-primary bg-primary bg-opacity-5' : 'border-gray-200'} rounded-lg p-3 cursor-pointer`}
                onClick={() => formik.setFieldValue('payment_method', 'paypal')}
              >
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="paypal"
                    name="payment_method"
                    value="paypal"
                    checked={formik.values.payment_method === 'paypal'}
                    onChange={formik.handleChange}
                    className="h-4 w-4 text-primary border-gray-300"
                  />
                  <label htmlFor="paypal" className="ml-2 block text-sm font-medium text-gray-700 cursor-pointer">
                    PayPal
                  </label>
                </div>
              </div>
            </div>
            {formik.touched.payment_method && formik.errors.payment_method && (
              <p className="mt-1 text-sm text-red-500">{formik.errors.payment_method}</p>
            )}
          </div>
        )}
        
        {/* Price summary */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <div className="flex justify-between mb-2">
            <span className="text-gray-600">Tour Price</span>
            <span>${tour.price} × {formik.values.adults + formik.values.children}</span>
          </div>
          <div className="border-t border-gray-200 my-2 pt-2 flex justify-between font-semibold">
            <span>Total</span>
            <span>${calculateTotal(formik.values.adults, formik.values.children)}</span>
          </div>
        </div>
        
        {/* Book Now button */}
        <button
          type="submit"
          disabled={bookingMutation.isLoading}
          className="w-full bg-primary hover:bg-primary-dark text-white py-3 px-4 rounded-md transition-colors duration-300 flex items-center justify-center"
        >
          {bookingMutation.isLoading ? (
            'Processing...'
          ) : (
            <>
              {formik.values.pay_later ? (
                <>
                  <BanknotesIcon className="h-5 w-5 mr-2" />
                  {isLoggedIn ? 'Reserve Now, Pay Later' : 'Login & Reserve'}
                </>
              ) : (
                <>
                  <CreditCardIcon className="h-5 w-5 mr-2" />
                  {isLoggedIn ? 'Book Now' : 'Login & Book'}
                </>
              )}
            </>
          )}
        </button>
        
        {/* Security note */}
        <div className="mt-4 flex items-center justify-center text-sm text-gray-500">
          <ShieldCheckIcon className="h-4 w-4 mr-1" />
          <span>
            {formik.values.pay_later 
              ? 'Reservation valid for 7 days' 
              : 'Secure payment & free cancellation'}
          </span>
        </div>
        
        {/* Pay later warning */}
        {formik.values.pay_later && (
          <div className="mt-3 flex items-center justify-center text-xs text-amber-700 bg-amber-50 p-2 rounded-md">
            <ExclamationCircleIcon className="h-4 w-4 mr-1 text-amber-500" />
            <span>Booking will be automatically canceled if not paid by {format(new Date(paymentDueDate), 'MMM d, yyyy')}</span>
          </div>
        )}
      </form>
    </div>
  );
};

export default BookingForm;
